import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, TemplateRef, inject } from '@angular/core';
import { I18NextPipe } from 'angular-i18next';
import { TreeElement } from 'mbs-ui-kit';
import { ModalService, ModalSettings } from 'mbs-ui-kit/modal/modal.service';
import { FullBackupData, IncrementalData } from 'mbs-ui-shared@models/backup/schedule-models';
import { ItemTypeEnum } from 'mbs-ui-shared@models/backup/storages-type';
import { AgentType } from 'mbs-ui-shared@models/Computer';
import { AzureAccessTierType, StorageClass, StorageType } from 'mbs-ui-shared@models/StorageType.enum';
import { AsyncRemoteCommandService } from 'mbs-ui-shared@services/async-remote-command/async-remote-command.service';
import { RmCommandsService } from 'mbs-ui-shared@services/rm-commands.service';
import { BehaviorSubject, Observable, Subject, concatMap, noop, of } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import { SelectHostStepValue } from 'wizards/models/select-host-models';
import { VirtualMachinesSelectedWithNumbersType, VirtualMachinesType } from 'wizards/models/select-virtual-machines-models';
import { AgentlessProtectionMode, ArchiveType } from '../models/backup-to-restore-models';

export type PlanForRestore = {
  bunchId: string;
  bunchName: string;
  type: string;
  firstRestoreDate: string;
  lastRestoreDate: string;
  originalSize: number;
  sizeOnStorage: number;
  totalSavings: number;
  isNBF: boolean;
  isCanBeDeleted: boolean;
  isPlanExists: boolean;
  retentionKeepBackupFor: number;
  lastBackupStatus: number;
  isEncrypted: boolean;
  countOfSuccessfulRestorePoints: number;
  countOfWarningRestorePoints: number;
  countOfErrorRestorePoints: number;
  itemType: ItemTypeEnum;
  itemTypeString: string;
  displayName: string;
  id: string;
  path: string;
  isCanBeExpand: boolean;
};

export type PrefixForRestore = {
  displayName: string;
  id: string;
  isCanBeDeleted: boolean;
  isCanBeExpand: boolean;
  isCurrent: boolean;
  itemType: ItemTypeEnum;
  itemTypeString: string;
  name: string;
  path: string;
};

export type RequestForRestore = { data: { items: Array<PlanForRestore | PrefixForRestore> } };

enum CalculatedPropertyArea {
  hosts = 'hosts',
  storage = 'storage',
  options = 'options',
  schedule = 'schedule',
  retention = 'retention',
  wizard = 'wizard',
  ABTest = 'ABTest'
}
export interface CalculatedPropertyStorage {
  name: string;
  type: StorageType;
  isPrivateStorage: boolean;
  storageClass: StorageClass;
  azureAccessTierType: AzureAccessTierType;
  minPeriod: number;
  immutabilityPeriodDays: number;
  invalidFFIStorage: string;
  canShowImmutabilityByStorage: boolean;
  isLocal?: boolean;
  hybridIsLocal?: boolean;
  hybridName?: string;
  hybridType?: StorageType;
}
interface CalculatedPropertyOptions {
  syntheticFull: boolean;
}
export interface CalculatedPropertySchedule {
  isFFI: boolean;
  isSimpleMode: boolean;
  isAdvancedMode: boolean;
  isManualMode: boolean;
  incrementalDataEnabled: boolean;
  scheduleInfo: { simple: string; incremental: string; full: string };
  incrementalSchedule: IncrementalData;
  fullSchedule: FullBackupData;
  forceDeactivateFFI: boolean;
}
export interface CalculatedPropertyRetention {
  isGFSMode: boolean;
  alerts: Partial<
    Record<
      | 'keepBackupFFIAlert'
      | 'keepBackupStorageObjectLockAlert'
      | 'keepBackupFullAlert'
      | 'needMinPeriodAlert'
      | 'intelligentRetention'
      | 'needRetentionForGFSAlert',
      string | boolean
    >
  >;
}
interface CalculatedPropertyWizard {
  userInteractWithSteps: boolean;
}
interface CalculatedPropertyABTest {
  DestinationPlusRetentionStep: boolean;
}
type CalculatedPropertyKey = Exclude<string, CalculatedPropertyArea>;

export type CheckLicenseResult = { usedSocketsCount: number; availableSocketsCount: number; isOk: boolean; message?: string };

export enum WindowsFeatures {
  hyperV,
  oneDrive,
  vMware,
  hyperVDiskListingSupported,
  hyperVChangedBlockTracking,
  hyperVApplicationProcessingOptions,
  hyperVCluster
}

export enum FilterRemoteCommand {
  Actual,
  Changed,
  Deleted,
  All
}

interface ParamsForAsync {
  isAsync?: boolean;
  requestId?: string;
}

interface AgentRemoteCommandParams {
  agentType: string;
  commandType: string;
  params: ParamsForAsync;
}

interface AgentHttpClientMethodOptions {
  headers?:
    | HttpHeaders
    | {
        [header: string]: string | string[];
      };
}

export interface GetBackupContentParams extends AgentRemoteCommandParams {
  params: ParamsForAsync & {
    SessionId?: string;
    ConnectionId?: string;
    RestoreSourcePrefix?: string;
    BunchId?: string;
    RestorePointDateUtc?: string;
    ContentFilter?: string;
    Filter?: FilterRemoteCommand;
    ForceRefresh?: boolean;
    Password?: string;
    password?: string;
    login?: string;
    Search?: string;
    SearchPattern?: string;
    path: string;
    offset: number;
    limit: number;
    order: string;
    BitlockerPasswordType?: BitLockerPasswordType;
    BitLockerPasswordValue?: string;
  };
}

export enum BitLockerPasswordType {
  Unset = 0,
  Password = 1,
  RecoveryPassword = 2,
  KeyFile = 3,
  CleanKey = 4
}

export interface ParamsForRCRestoreMethods extends AgentRemoteCommandParams {
  agentType: 'backup';
  commandType: 'GetBunchList' | 'GetRestoreSourcePrefix' | 'GetRestorePointList' | 'GetDiskInfo';
  params: ParamsForAsync & {
    ConnectionId?: string;
    RestoreSourcePrefix?: string;
    Type?: ArchiveType;
    BunchId?: string;
    GenerationId?: string;
    RestorePointDateUtc?: string;
    SessionId?: string;
    SourceHid?: string;
    SourceUserId?: string;
  };
}

export interface ParamsForDeepSync extends AgentRemoteCommandParams {
  agentType: 'backup';
  commandType: 'RunDeepSync' | 'GetDeepSyncStatus';
  params: ParamsForAsync & {
    connectionId?: string;
    restoreSourcePrefix?: string;
    bunchId?: string;
    restorePointDateUtc?: string;
    Password?: string;
    SessionId?: string;
  };
}

export interface ParamsForTestCredentials extends AgentRemoteCommandParams {
  agentType: 'backup';
  commandType: 'CheckVMCredentials';
  params: ParamsForAsync & { Type: string; Server: string; Login: string; Password: string };
}

export interface ParamsForGetVirtualMachinesOrDisksList extends AgentRemoteCommandParams {
  agentType: 'backup';
  commandType: 'GetVirtualMachineList' | 'GetVirtualMachineDiskList' | 'GetVirtualMachineDiskList2';
  params: ParamsForAsync & {
    PlanId: string;
    VirtualMachineIds?: string[];
    Type: string;
    Server: string;
    Login: string;
    Password: string;
    IsCluster: boolean;
    Order?: string;
    Offset?: number;
    Limit?: number;
    SessionId?: string;
  };
}

export interface ParamsForGetDataStore extends AgentRemoteCommandParams {
  agentType: 'backup';
  commandType: 'GetVirtualMachineDataStoreList';
  params: ParamsForAsync & {
    PlanId: string;
    Type: VirtualMachinesType | string;
    Server: string;
    Login: string;
    Password: string;
    IsCluster: boolean;
    Offset: number;
    Limit: number;
    Order: string;
    Search: string;
    Host: string;
  };
}

export interface ParamsForGetVMNetworks extends AgentRemoteCommandParams {
  agentType: 'backup';
  commandType: 'GetVMNetworks';
  params: ParamsForAsync & {
    PlanId: string;
    Type: VirtualMachinesType | string;
    Server: string;
    Login: string;
    Password: string;
    Host: string;
    Offset: number;
    Limit: number;
  };
}

export interface ParamsForGetGuestOS extends AgentRemoteCommandParams {
  agentType: 'backup';
  commandType: 'GetVMGuestTypes';
  params: ParamsForAsync & {
    PlanId: string;
    Type: VirtualMachinesType | string;
    Server: string;
    Login: string;
    Password: string;
    Host: string;
    Offset: number;
    Limit: number;
  };
}

export interface ParamsForGetCardModel extends AgentRemoteCommandParams {
  agentType: 'backup';
  commandType: 'GetVMNetworkCards';
  params: ParamsForAsync & {
    PlanId: string;
    Type: VirtualMachinesType | string;
    Server: string;
    Login: string;
    Password: string;
    Host: string;
    Offset: number;
    Limit: number;
  };
}

export interface ParamsForCheckVMSocketLicenses extends AgentRemoteCommandParams {
  agentType: 'backup';
  commandType: 'CheckVMSocketLicenses' | 'CheckVMSocketLicensesById';
  params: ParamsForAsync & {
    Type: VirtualMachinesType;
    Server: string;
    UserName: string;
    Password: string;
    PlanId: string;
    VMBackupType: VirtualMachinesSelectedWithNumbersType;
    Machines: Array<string | { Name: string; Id: string }>;
  };
}

export interface ParamsForTreeData extends AgentRemoteCommandParams {
  params: ParamsForAsync & {
    connectionId?: string;
    planId?: string;
    path?: string;
    Password?: string;
    login?: string;
    password?: string;
    offset?: number;
    limit?: number;
    order?: string;
    isAsync?: boolean;
  };
}

export interface ParamsForIsWindowsFeatureEnabled extends AgentRemoteCommandParams {
  commandType: 'IsWindowsFeatureEnabled';
  params: ParamsForAsync & {
    Features: WindowsFeatures[];
  };
}

export interface ParamsForInstallFeature extends AgentRemoteCommandParams {
  commandType: 'InstallFeature';
  params: ParamsForAsync & {
    Feature: WindowsFeatures;
  };
}

export interface ParamsForDeleteBackupObject extends AgentRemoteCommandParams {
  agentType: 'backup';
  commandType: 'DeleteBackupObject';
  params: ParamsForAsync & { ObjectsToDelete?: string[] };
}

export interface ParamsForVerifyBackupPassword extends AgentRemoteCommandParams {
  agentType: 'backup';
  commandType: 'VerifyBackupPassword';
  params: ParamsForAsync & {
    path: string;
    password: string;
    SessionId?: string;
  };
}

export interface ParamsForGetDatabases extends AgentRemoteCommandParams {
  agentType: 'backup';
  commandType: 'GetDatabases';
  params: ParamsForAsync & {
    isWindowsAuth: boolean;
    instanceName: string;
    userName: string;
    password: string;
    planId: string;
  };
}
export interface ParamsForStartPlan extends AgentRemoteCommandParams {
  agentType: 'backup';
  commandType: 'StartPlan';
  params: ParamsForAsync & { PlanId: string; Mode: number };
}
export interface ParamsForRestoreBackupPlan extends AgentRemoteCommandParams {
  agentType: 'backup';
  commandType: 'RestoreBackupPlan';
  params: ParamsForAsync & { path: string };
}

export interface ParamsForVMDiskList extends AgentRemoteCommandParams {
  agentType: 'backup';
  commandType: 'GetBackupVirtualMachineDiskList';
  params: ParamsForAsync & {
    machineNames: string[];
    path: string;
    password: string;
    order: string;
    SessionId?: string;
  };
}

export interface ParamsForInitForeignBrowsingSession extends AgentRemoteCommandParams {
  agentType: 'backup';
  commandType: 'InitForeignBrowsingSession';
  params: ParamsForAsync & { UserId: string };
}

export interface ParamsForAgentlessPassword extends AgentRemoteCommandParams {
  agentType: 'backup';
  commandType: 'GetAgentlessPassword';
  params: ParamsForAsync & { SessionId: string };
}

export interface ParamsForStartQuickRestore extends AgentRemoteCommandParams {
  agentType: 'backup';
  commandType: 'StartQuickRestore';
  params: ParamsForAsync & {
    RestorePointObjectPath: string;
    UseCustomPrefix: boolean;
    ItemsToRestore: string[];
    Password: string;
    StartAfterCreate: boolean;
    Destination: string;
    AgentlessRestore?: boolean;
    AgentlessSessionId?: string;
    AgentlessProtectionMode?: AgentlessProtectionMode;
  };
}

export type getRemoteCommandDataType =
  | ParamsForAgentlessPassword
  | ParamsForTreeData
  | ParamsForTestCredentials
  | ParamsForGetCardModel
  | ParamsForGetGuestOS
  | ParamsForGetVMNetworks
  | ParamsForGetDataStore
  | ParamsForGetVirtualMachinesOrDisksList
  | ParamsForCheckVMSocketLicenses
  | ParamsForRCRestoreMethods
  | ParamsForIsWindowsFeatureEnabled
  | ParamsForInstallFeature
  | ParamsForDeleteBackupObject
  | ParamsForVerifyBackupPassword
  | ParamsForGetDatabases
  | ParamsForStartPlan
  | ParamsForStartQuickRestore
  | ParamsForRestoreBackupPlan
  | ParamsForVMDiskList
  | ParamsForInitForeignBrowsingSession;

export type MachinesDisksLoadedStatuses = { voidMachines: string[]; allVoid: boolean; loadingDisks: boolean };
export type RestoreSourceData = { folders: string[]; treeData: TreeElement[] };

@Injectable({
  providedIn: 'root'
})
export class WizardStepsService {
  private http = inject(HttpClient);
  private i18nPipe = inject(I18NextPipe);
  private modalService = inject(ModalService);
  private asyncRemoteCommandService = inject(AsyncRemoteCommandService);
  private rmCommandsService = inject(RmCommandsService);

  public backupPlanName: string;
  public treeData$: BehaviorSubject<RestoreSourceData> = new BehaviorSubject(null);
  public deepSyncSuccess$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public hyperVNotFoundError$: BehaviorSubject<{ title: string; content: string }> = new BehaviorSubject(null);
  public headers = { 'Content-Type': 'application/json; charset=utf-8' };

  private featureStatuses: { [HID_features: string]: Observable<any> } = {};
  private stepCalculatedProperties: {
    areas: { [area: string]: Subject<unknown> };
    observables: { [area: string]: { [key: CalculatedPropertyKey]: Subject<unknown> } };
    values: { [area: string]: { [key: CalculatedPropertyKey]: unknown } };
  } = this.getStepCalculatedProperties();

  private errorModalIsOpen = false;

  reset(): void {
    this.stepCalculatedProperties = this.getStepCalculatedProperties();
  }

  updateProperty<K extends keyof SelectHostStepValue>(area: 'hosts', key: keyof SelectHostStepValue, value: SelectHostStepValue[K]): void;
  updateProperty<K extends keyof CalculatedPropertyStorage>(
    area: 'storage',
    key: keyof CalculatedPropertyStorage,
    value: CalculatedPropertyStorage[K]
  ): void;
  updateProperty<K extends keyof CalculatedPropertyOptions>(
    area: 'options',
    key: keyof CalculatedPropertyOptions,
    value: CalculatedPropertyOptions[K]
  ): void;
  updateProperty<K extends keyof CalculatedPropertySchedule>(
    area: 'schedule',
    key: keyof CalculatedPropertySchedule,
    value: CalculatedPropertySchedule[K]
  ): void;
  updateProperty<K extends keyof CalculatedPropertyRetention>(
    area: 'retention',
    key: keyof CalculatedPropertyRetention,
    value: CalculatedPropertyRetention[K]
  ): void;
  updateProperty<K extends keyof CalculatedPropertyWizard>(
    area: 'wizard',
    key: keyof CalculatedPropertyWizard,
    value: CalculatedPropertyWizard[K]
  ): void;
  updateProperty<K extends keyof CalculatedPropertyABTest>(
    area: 'ABTest',
    key: keyof CalculatedPropertyABTest,
    value: CalculatedPropertyABTest[K]
  ): void;
  updateProperty(area: keyof typeof CalculatedPropertyArea, key: CalculatedPropertyKey, value: unknown): void {
    const property = this.getProperty(area, key);
    const areaMap = this.stepCalculatedProperties.values[area];

    areaMap[key] = value;
    property.next(value);
    this.stepCalculatedProperties.areas[area].next({ ...areaMap });
  }

  pipeABTest(): Observable<Partial<CalculatedPropertyABTest>> {
    return this.pipeProperty('ABTest');
  }

  pipeSchedule(): Observable<Partial<CalculatedPropertySchedule>> {
    return this.pipeProperty('schedule');
  }

  pipeStorage(): Observable<Partial<CalculatedPropertyStorage>> {
    return this.pipeProperty('storage');
  }

  pipeRetention(): Observable<Partial<CalculatedPropertyRetention>> {
    return this.pipeProperty('retention');
  }

  pipeProperty(area: 'hosts'): Observable<Partial<SelectHostStepValue>>;
  pipeProperty(area: 'storage'): Observable<Partial<CalculatedPropertyStorage>>;
  pipeProperty(area: 'options'): Observable<Partial<CalculatedPropertyOptions>>;
  pipeProperty(area: 'schedule'): Observable<Partial<CalculatedPropertySchedule>>;
  pipeProperty(area: 'retention'): Observable<Partial<CalculatedPropertyRetention>>;
  pipeProperty(area: 'wizard'): Observable<Partial<CalculatedPropertyWizard>>;
  pipeProperty(area: 'ABTest'): Observable<Partial<CalculatedPropertyABTest>>;
  pipeProperty(area: keyof typeof CalculatedPropertyArea): Observable<unknown>;
  pipeProperty<K extends keyof CalculatedPropertyStorage>(area: 'storage', key: K): Observable<CalculatedPropertyStorage[K]>;
  pipeProperty<K extends keyof CalculatedPropertyOptions>(area: 'options', key: K): Observable<CalculatedPropertyOptions[K]>;
  pipeProperty<K extends keyof CalculatedPropertySchedule>(area: 'schedule', key: K): Observable<CalculatedPropertySchedule[K]>;
  pipeProperty<K extends keyof CalculatedPropertyRetention>(area: 'retention', key: K): Observable<CalculatedPropertyRetention[K]>;
  pipeProperty<K extends keyof CalculatedPropertyWizard>(area: 'wizard', key: K): Observable<CalculatedPropertyWizard[K]>;
  pipeProperty<K extends keyof CalculatedPropertyABTest>(area: 'ABTest', key: K): Observable<CalculatedPropertyABTest[K]>;
  pipeProperty(area: keyof typeof CalculatedPropertyArea, key: CalculatedPropertyKey): Observable<unknown>;
  pipeProperty(...args): Observable<unknown> {
    const withKey = args.length === 2;
    const [area, key] = withKey ? args : [...args, null];

    if (!withKey) {
      return this.getArea(area).asObservable().pipe(shareReplay(1));
    }

    return this.getProperty(area, key).asObservable().pipe(shareReplay(1));
  }

  getSnapshot(area: 'hosts'): Partial<SelectHostStepValue>;
  getSnapshot(area: 'storage'): Partial<CalculatedPropertyStorage>;
  getSnapshot(area: 'options'): Partial<CalculatedPropertyOptions>;
  getSnapshot(area: 'schedule'): Partial<CalculatedPropertySchedule>;
  getSnapshot(area: 'retention'): Partial<CalculatedPropertyRetention>;
  getSnapshot(area: 'wizard'): Partial<CalculatedPropertyWizard>;
  getSnapshot(area: 'ABTest'): Partial<CalculatedPropertyABTest>;
  getSnapshot(area: keyof typeof CalculatedPropertyArea): unknown;
  getSnapshot<K extends keyof CalculatedPropertyStorage>(area: 'storage', key: K): CalculatedPropertyStorage[K];
  getSnapshot<K extends keyof CalculatedPropertyOptions>(area: 'options', key: K): CalculatedPropertyOptions[K];
  getSnapshot<K extends keyof CalculatedPropertySchedule>(area: 'schedule', key: K): CalculatedPropertySchedule[K];
  getSnapshot<K extends keyof CalculatedPropertyRetention>(area: 'retention', key: K): CalculatedPropertyRetention[K];
  getSnapshot<K extends keyof CalculatedPropertyWizard>(area: 'wizard', key: K): CalculatedPropertyWizard[K];
  getSnapshot<K extends keyof CalculatedPropertyABTest>(area: 'ABTest', key: K): CalculatedPropertyABTest[K];
  getSnapshot(area: keyof typeof CalculatedPropertyArea, key: CalculatedPropertyKey): unknown;
  getSnapshot(...args): unknown {
    const withKey = args.length === 2;
    const [area, key] = withKey ? args : [...args, null];

    if (!withKey) {
      return this.getAreaSnapshot(area);
    }

    return this.getPropertySnapshot(area, key);
  }

  prepareParams(params: getRemoteCommandDataType): void {
    const changeParamsRules = [
      {
        key: 'PlanId',
        replaceTo: null,
        wrongValues: ['']
      }
    ];

    changeParamsRules.forEach((rule) => {
      if (rule.wrongValues.includes((params as ParamsForCheckVMSocketLicenses)?.params[rule.key])) {
        (params as ParamsForCheckVMSocketLicenses).params[rule.key] = rule.replaceTo;
      }
    });
  }

  getRemoteCommandDataByUser(params: getRemoteCommandDataType['params'], userId: string): Observable<any> {
    return this.runSyncCommand('post', `/api/users/${userId}/get-backup-content`, params, {
      headers: this.headers
    });
  }

  getRemoteCommandData(params: getRemoteCommandDataType, hid: string, needLegacyAsync = false): Observable<any> {
    const needAsync = [
      'CheckVMSocketLicenses',
      'DeleteBackupObject',
      'GetBackupContent',
      'GetBackupVirtualMachineDiskList',
      'getComputerContent',
      'GetDatabases',
      'GetRestorePointList',
      'ParamsForGetCardModel',
      'ParamsForGetGuestOS',
      'ParamsForGetVMNetworks',
      'GetVirtualMachineDataStoreList',
      'GetVirtualMachineDiskContent',
      'GetVirtualMachineDiskList',
      'GetVirtualMachineDiskList2',
      'GetVirtualMachineHostList',
      'GetVirtualMachineList',
      'VerifyBackupPassword'
    ].includes(params.commandType);

    this.prepareParams(params);

    if (needAsync && !window['callAgentSync']) {
      return this.asyncRemoteCommandService[needLegacyAsync ? 'runAsyncCommandLegacy' : 'runAsyncCommand']({
        hid,
        params,
        options: {
          headers: this.headers
        }
      });
    }

    return this.rmCommandsService.remoteCommandByHid(hid, params, false, { headers: this.headers });
  }

  VMFeatureTest(hid: string, feature = WindowsFeatures.hyperV): Observable<any> {
    return this.getWindowFeaturesRequest(hid, feature).pipe(
      concatMap((data) => {
        const newData = { ...data };

        // Typical response for single feature contains result in data field
        // the Response for group features contains result in data.result field as a map
        // all we need to do for backward capability - is extract group result
        if (data?.data?.result) {
          newData.data = data.data.result[WindowsFeatures[feature]];
        }

        return of(newData);
      })
    );
  }

  showHyperVInfoModal(forRestoreVerification = true): void {
    const modalSettings: ModalSettings = {
      header: { title: this.i18nPipe.transform('wizards:hyperVNotInstalled') },
      footer: { okButton: { text: 'Ok' }, cancelButton: { show: false } }
    };
    const reasonKey = forRestoreVerification
      ? 'wizards:hyperVNotInstalledModal.restoreVerification'
      : 'wizards:hyperVNotInstalledModal.backup';
    const message = `${this.i18nPipe.transform('wizards:hyperVNotInstalledModal.common')} <br/>
      ${this.i18nPipe.transform(reasonKey)}`;
    this.modalService.open(modalSettings, message).finally(noop);
  }

  infoModalShow(title: string, body: TemplateRef<any> | string): void {
    const modalSettings: ModalSettings = {
      header: { title: title },
      footer: { okButton: { text: 'Ok' }, cancelButton: { show: false } }
    };

    this.modalService.open(modalSettings, body).then().catch(noop);
  }

  openErrorModal(error: { title: string; content: string }): void {
    if (this.errorModalIsOpen) return;

    const settings: ModalSettings = {
      header: { title: error.title },
      footer: {
        okButton: { show: false },
        cancelButton: { text: this.i18nPipe.transform('shared:buttons.close') }
      }
    };

    this.errorModalIsOpen = true;
    this.modalService.open(settings, error.content).finally(() => (this.errorModalIsOpen = false));
  }

  private runSyncCommand(
    method: 'post',
    url: string,
    params: AgentRemoteCommandParams | AgentRemoteCommandParams['params'],
    options: AgentHttpClientMethodOptions
  ): Observable<any> {
    return this.http[method](url, JSON.stringify(params), options) as Observable<any>;
  }

  private getWindowFeaturesRequest(hid: string, feature: WindowsFeatures): Observable<any> {
    const Features = this.getWindowFeaturesPack(feature);
    const key = `${hid}:${Features.join('-')}`;
    const params: ParamsForIsWindowsFeatureEnabled = {
      agentType: AgentType.Backup,
      commandType: 'IsWindowsFeatureEnabled',
      params: {
        Features: this.getWindowFeaturesPack(feature)
      }
    };

    this.featureStatuses[key] =
      this.featureStatuses[key] ||
      this.rmCommandsService.remoteCommandByHid(hid, params, false, { headers: this.headers }).pipe(shareReplay(1));

    return this.featureStatuses[key];
  }

  private getWindowFeaturesPack(feature: WindowsFeatures): WindowsFeatures[] {
    if (feature === WindowsFeatures.oneDrive) {
      return [WindowsFeatures.oneDrive];
    }

    if (feature === WindowsFeatures.vMware) {
      return [WindowsFeatures.vMware];
    }

    return [
      WindowsFeatures.hyperV,
      WindowsFeatures.hyperVDiskListingSupported,
      WindowsFeatures.hyperVChangedBlockTracking,
      WindowsFeatures.hyperVApplicationProcessingOptions,
      WindowsFeatures.hyperVCluster
    ];
  }

  private getStepCalculatedProperties(): typeof this.stepCalculatedProperties {
    return Object.keys(CalculatedPropertyArea).reduce(
      (result, current) => {
        result.areas[current] = new Subject<unknown>();
        result.observables[current] = {};
        result.values[current] = {};

        return result;
      },
      { areas: {}, observables: {}, values: {} }
    );
  }

  private getArea(area: keyof typeof CalculatedPropertyArea): Subject<unknown> {
    return this.stepCalculatedProperties.areas[area];
  }

  private getProperty(area: keyof typeof CalculatedPropertyArea | null, key: CalculatedPropertyKey): Subject<unknown> | null {
    const areaMap = this.stepCalculatedProperties.observables[area];
    const result = new Subject<unknown>();

    if (areaMap[key]) {
      return areaMap[key];
    }

    areaMap[key] = result;

    return result;
  }

  private getAreaSnapshot(area: keyof typeof CalculatedPropertyArea): unknown {
    return this.stepCalculatedProperties.values[area];
  }

  private getPropertySnapshot(area: keyof typeof CalculatedPropertyArea | null, key: CalculatedPropertyKey): unknown {
    const areaMap = this.stepCalculatedProperties.values[area];

    return areaMap[key];
  }
}
